import type { FC, ReactNode } from 'react'
import type { Article } from '@/types'

import { Reveal } from '@/components/generals/reveal'
import { Rule } from '@/components/generals/rule'
import { SplitText } from '@/components/generals/split-text'
import { media } from '@/constants/media'
import { readingTime } from '@/lib/reading-time'

interface ArticleHeroProps {
    article: Article
}

const AUTHOR = 'Jin Yuen'

export const ArticleHero: FC<ArticleHeroProps> = ({ article }): ReactNode => {
    return (
        <section
            id='article-hero'
            className='mx-auto w-full max-w-[1920px] px-5 pb-10 pt-[calc(var(--nav-h)_+_44px)] lg:px-[33px] lg:pb-14 lg:pt-[calc(var(--nav-h)_+_92px)]'
        >
            <div className='grid gap-4 lg:grid-cols-4 lg:gap-5'>
                <Reveal from='down' distance={16} duration={0.9} delay={0.12}>
                    <span className='t-mono text-brand'>
                        {article.category}
                    </span>
                </Reveal>

                <div className='lg:col-span-3'>
                    <SplitText
                        as='h1'
                        text={article.title}
                        mode='chars'
                        trigger='mount'
                        delay={0.28}
                        stagger={0.012}
                        duration={1.15}
                        className='t-display text-ink'
                        style={{ fontSize: 'clamp(38px, 7vw, 110px)' }}
                    />
                </div>
            </div>

            <Rule className='mt-9 lg:mt-16' delay={0.5} />

            <div className='mt-6 grid gap-4 lg:grid-cols-4 lg:gap-5'>
                <Reveal from='up' distance={22} duration={1} delay={0.06}>
                    <time dateTime={article.datetime} className='t-mono text-ash'>
                        {article.date}
                    </time>
                </Reveal>

                <Reveal from='up' distance={22} duration={1} delay={0.14}>
                    <span className='t-mono text-ash'>
                        {readingTime(article.body)}
                    </span>
                </Reveal>

                <Reveal
                    from='up'
                    distance={22}
                    duration={1}
                    delay={0.22}
                    className='lg:col-span-2 lg:justify-self-end'
                >
                    <span className='flex items-center gap-3'>
                        <img
                            src={media.people.founderAvatar}
                            alt=''
                            aria-hidden='true'
                            width={32}
                            height={32}
                            loading='lazy'
                            decoding='async'
                            className='size-8 rounded-full object-cover'
                        />

                        <span className='t-mono text-ink'>
                            {AUTHOR}
                        </span>
                    </span>
                </Reveal>
            </div>
        </section>
    )
}
